// ===========================================
// ChannelToggles
// ===========================================
// Reusable component for selecting Save the Date delivery channels.
// EMAIL is always on and disabled in Phase 1.
// SMS and WHATSAPP are disabled (with tooltip) if not yet available.

import {
  FormGroup,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useAvailableChannels } from '../../hooks/useSaveTheDates';
import type { SaveTheDateChannel } from '../../api/saveTheDates.api';

// ===========================================
// TYPES
// ===========================================

export interface ChannelTogglesProps {
  value: SaveTheDateChannel[];
  onChange: (channels: SaveTheDateChannel[]) => void;
  disabled?: boolean;
}

// ===========================================
// CHANNEL LABELS
// ===========================================

const CHANNEL_LABELS: Record<SaveTheDateChannel, string> = {
  EMAIL: 'Email',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
};

const ALL_CHANNELS: SaveTheDateChannel[] = ['EMAIL', 'SMS', 'WHATSAPP'];

// ===========================================
// COMPONENT
// ===========================================

export default function ChannelToggles({ value, onChange, disabled = false }: ChannelTogglesProps) {
  const { data: availability, isLoading } = useAvailableChannels();

  const toggle = (channel: SaveTheDateChannel, checked: boolean) => {
    if (checked) {
      onChange([...value, channel]);
    } else {
      onChange(value.filter((c) => c !== channel));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading channels...
        </Typography>
      </Box>
    );
  }

  return (
    <FormGroup row>
      {ALL_CHANNELS.map((channel) => {
        const isEmail = channel === 'EMAIL';
        const channelAvailable = isEmail || (availability?.[channel]?.available ?? false);
        const isChecked = value.includes(channel);
        const isDisabled = disabled || isEmail || !channelAvailable;

        const switchEl = (
          <FormControlLabel
            key={channel}
            label={CHANNEL_LABELS[channel]}
            control={
              <Switch
                checked={isChecked}
                disabled={isDisabled}
                onChange={(e) => toggle(channel, e.target.checked)}
              />
            }
          />
        );

        if (!channelAvailable && !isEmail) {
          return (
            <Tooltip
              key={channel}
              title="Available in a future release"
              placement="top"
              arrow
            >
              <span>{switchEl}</span>
            </Tooltip>
          );
        }

        if (isEmail) {
          return (
            <Tooltip
              key={channel}
              title="Email is always included"
              placement="top"
              arrow
            >
              <span>{switchEl}</span>
            </Tooltip>
          );
        }

        return switchEl;
      })}
    </FormGroup>
  );
}
